// Split a chat message body into plain and link segments (8 Sep 2026). Mirrors
// splitLinks in the portal/staff apps' LinkedText.tsx — keep the three in step.
//
// Only the http(s) and www. forms are recognised — bare domains are left alone so
// "file.pdf" or "e.g." never turn into links. Trailing sentence punctuation and an
// unbalanced closing bracket stay outside the link.
export type LinkSegment = { text: string; url?: string };

const LINK_RE = /(?:https?:\/\/|www\.)[^\s<>"'`]+/gi;
const TRAILING_PUNCT = /[.,;:!?'"]+$/;
const SCHEME_ONLY = /^(?:https?:\/\/|www\.)$/i;

function count(s: string, ch: string): number {
  return s.split(ch).length - 1;
}

function trimLink(raw: string): string {
  let s = raw.replace(TRAILING_PUNCT, "");
  for (const [open, close] of [["(", ")"], ["[", "]"]] as const) {
    while (s.endsWith(close) && count(s, close) > count(s, open)) {
      s = s.slice(0, -1).replace(TRAILING_PUNCT, "");
    }
  }
  return s;
}

export function splitLinks(text: string): LinkSegment[] {
  const out: LinkSegment[] = [];
  let last = 0;
  for (const m of text.matchAll(LINK_RE)) {
    const start = m.index ?? 0;
    // Mid-word or the tail of an email address ("kate@www.example.com") — leave it.
    if (start > 0 && /[A-Za-z0-9@]/.test(text[start - 1] ?? "")) continue;
    const link = trimLink(m[0]);
    if (SCHEME_ONLY.test(link)) continue;
    if (start > last) out.push({ text: text.slice(last, start) });
    out.push({
      text: link,
      url: link.toLowerCase().startsWith("www.") ? `https://${link}` : link,
    });
    last = start + link.length;
  }
  if (last < text.length) out.push({ text: text.slice(last) });
  return out;
}
